import * as React from 'react';

import { gql, useQuery } from '@apollo/client';

import type { SkillTag } from '@coursera/grpc-types-skills/coursera/proto/skills/tags/v2/skill_tag';
import type {
  MultiGetSkillTagsRequest,
  MultiGetSkillTagsResponse,
} from '@coursera/grpc-types-skills/coursera/proto/skills/tags/v2/skills_tags_api';

import { useEnterpriseLearningPathPageData } from 'bundles/enterprise-learning-path-description-page/contexts/EnterpriseLearningPathContext';
import EnterpriseLearningPathSkillsContext from 'bundles/enterprise-learning-path-description-page/contexts/EnterpriseLearningPathSkillsContext';

export const enterpriseLearningPathSkillsQuery = gql`
  query enterpriseLearningPathSkillsQuery($input: MultiGetSkillTags!) {
    getSkillTags(input: $input)
      @rest(type: "MultiGetSkillTags", path: "grpc/skills/tags/v2/SkillsTagsAPI/MultiGetSkillTags", method: "POST") {
      contentsWithTags {
        skillTaggableContent {
          skillTaggableCourse
        }
        skillTags {
          skillId
          skillName
        }
      }
    }
  }
`;

type PropsToComponent = {
  children: React.ReactNode;
};

const EnterpriseLearningPathSkillsDataProvider = ({ children }: PropsToComponent) => {
  const pageData = useEnterpriseLearningPathPageData();
  const courseIds = pageData?.enterpriseBadge?.badgeActivities.map((activity) => activity.id) ?? [];

  const { data, loading, error } = useQuery<
    { getSkillTags: MultiGetSkillTagsResponse },
    { input: MultiGetSkillTagsRequest }
  >(enterpriseLearningPathSkillsQuery, {
    variables: {
      input: {
        skillTaggableContents: courseIds.map((courseId) => ({ skillTaggableCourse: { courseId } })),
      },
    },
    skip: !courseIds.length,
  });

  if (!data || !data.getSkillTags || !data.getSkillTags.contentsWithTags) {
    return <>{children}</>;
  }

  const courseSkillTagsMap = data.getSkillTags.contentsWithTags.reduce(
    (map, content) => {
      if (content?.skillTaggableContent?.skillTaggableCourse?.courseId) {
        map[content.skillTaggableContent.skillTaggableCourse.courseId] = content.skillTags;
      }
      return map;
    },
    {} as Record<string, SkillTag[]>
  );

  return (
    <EnterpriseLearningPathSkillsContext.Provider
      value={{
        courseSkillTagsMap,
        isSkillTagsLoading: loading,
        error,
      }}
    >
      {children}
    </EnterpriseLearningPathSkillsContext.Provider>
  );
};

export default EnterpriseLearningPathSkillsDataProvider;
